/**
 * Categorical Rhyme Detection Engine
 * Detects exactly one of 7 discrete rhyme categories per word pair
 */

import type { RhymeMatch } from './types';
import { RhymeCategory, CATEGORY_WEIGHTS } from './types';
import {
  getPhonemes,
  extractRhymeAnchor,
  stripStress,
  isVowel,
  isConsonant,
  isVoicedConsonant,
  isUnvoicedConsonant,
  getVoicingPartner
} from './phonetics';

/**
 * Options for rhyme category detection
 */
export interface RhymeCategoryOptions {
  useBackend?: boolean; // Enable backend lookup (default: true — local CMU dict removed in scope 13-04)
}

/**
 * Main categorical rhyme detector
 * Returns exactly ONE category with corresponding weight (no confidence scores)
 *
 * Uses extractRhymeAnchor (final syllable) as the canonical rhyme anchor,
 * aligned with getPhoneticSignature for grouping/detection consistency.
 *
 * Detection order (short-circuit once matched):
 * 1. PERFECT: Rhyming portions match exactly
 * 2. FAMILY_VOICED: Final consonant differs by voicing, original is voiced
 * 3. FAMILY_UNVOICED: Final consonant differs by voicing, original is unvoiced
 * 4. ADDITIVE: One phoneme added to coda
 * 5. SUBTRACTIVE: One phoneme removed from coda
 * 6. ASSONANCE: Stressed vowel matches, consonants differ
 * 7. CONSONANCE: Final consonant matches, vowel differs
 * 8. NONE: No match
 *
 * @param word1 - First word to compare
 * @param word2 - Second word to compare
 * @param options - Detection options (useBackend, etc.)
 */
export async function detectRhymeCategory(
  word1: string,
  word2: string,
  options: RhymeCategoryOptions = {}
): Promise<RhymeMatch> {
  const { useBackend = true } = options;  // Local CMU dict removed — backend is the phoneme source

  // Get full phoneme arrays for both words (with optional backend fallback)
  const phonemes1 = await getPhonemes(word1, useBackend);
  const phonemes2 = await getPhonemes(word2, useBackend);

  // If either word can't be phonetically analyzed, return NONE
  if (!phonemes1 || !phonemes2) {
    return {
      category: RhymeCategory.NONE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.NONE],
      word1,
      word2,
      phonemes1: phonemes1 || [],
      phonemes2: phonemes2 || []
    };
  }

  // Canonical anchor: final syllable (last vowel to end). Aligned with
  // getPhoneticSignature so grouping and detection can never disagree.
  const rhyme1 = extractRhymeAnchor(phonemes1);
  const rhyme2 = extractRhymeAnchor(phonemes2);

  // Short-circuit if either rhyming portion is empty
  if (rhyme1.length === 0 || rhyme2.length === 0) {
    return {
      category: RhymeCategory.NONE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.NONE],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 1. PERFECT: Rhyming portions match exactly
  if (phonemesMatch(rhyme1, rhyme2)) {
    return {
      category: RhymeCategory.PERFECT,
      weight: CATEGORY_WEIGHTS[RhymeCategory.PERFECT],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 2 & 3. FAMILY_VOICED / FAMILY_UNVOICED: Final consonant differs by voicing
  const voicingCategory = checkVoicingFamily(rhyme1, rhyme2);
  if (voicingCategory) {
    return {
      category: voicingCategory,
      weight: CATEGORY_WEIGHTS[voicingCategory],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 4. ADDITIVE: One phoneme added to coda
  if (isAdditive(rhyme1, rhyme2)) {
    return {
      category: RhymeCategory.ADDITIVE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.ADDITIVE],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 5. SUBTRACTIVE: One phoneme removed from coda
  if (isSubtractive(rhyme1, rhyme2)) {
    return {
      category: RhymeCategory.SUBTRACTIVE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.SUBTRACTIVE],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 6. ASSONANCE: Stressed vowel matches, consonants differ
  if (isAssonance(rhyme1, rhyme2)) {
    return {
      category: RhymeCategory.ASSONANCE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.ASSONANCE],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 7. CONSONANCE: Final consonant matches, vowel differs
  if (isConsonance(rhyme1, rhyme2)) {
    return {
      category: RhymeCategory.CONSONANCE,
      weight: CATEGORY_WEIGHTS[RhymeCategory.CONSONANCE],
      word1,
      word2,
      phonemes1,
      phonemes2
    };
  }

  // 8. NONE: No match
  return {
    category: RhymeCategory.NONE,
    weight: CATEGORY_WEIGHTS[RhymeCategory.NONE],
    word1,
    word2,
    phonemes1,
    phonemes2
  };
}

/**
 * Check if phoneme arrays match exactly (ignoring stress on vowels)
 */
function phonemesMatch(p1: string[], p2: string[]): boolean {
  if (p1.length !== p2.length) return false;

  for (let i = 0; i < p1.length; i++) {
    // For consonants, exact match required
    // For vowels, stress can differ
    const clean1 = stripStress(p1[i]);
    const clean2 = stripStress(p2[i]);
    if (clean1 !== clean2) return false;
  }

  return true;
}

/**
 * Check for voicing family rhyme (FAMILY_VOICED or FAMILY_UNVOICED)
 * Returns category if match, null otherwise
 */
function checkVoicingFamily(rhyme1: string[], rhyme2: string[]): RhymeCategory | null {
  // Must have same length and differ only in final consonant
  if (rhyme1.length !== rhyme2.length || rhyme1.length < 2) return null;

  // Check that all phonemes except the last match
  for (let i = 0; i < rhyme1.length - 1; i++) {
    if (stripStress(rhyme1[i]) !== stripStress(rhyme2[i])) {
      return null;
    }
  }

  // Last phoneme must be consonants that are voicing partners
  const last1 = rhyme1[rhyme1.length - 1];
  const last2 = rhyme2[rhyme2.length - 1];

  if (!isConsonant(last1) || !isConsonant(last2)) return null;

  const partner1 = getVoicingPartner(last1);
  if (!partner1 || stripStress(partner1) !== stripStress(last2)) {
    return null;
  }

  // Determine which category based on which original consonant is voiced
  if (isVoicedConsonant(last1)) {
    return RhymeCategory.FAMILY_VOICED;
  } else if (isUnvoicedConsonant(last1)) {
    return RhymeCategory.FAMILY_UNVOICED;
  }

  return null;
}

/**
 * Check for additive rhyme (one phoneme added to coda)
 * Example: detectRhymeCategory('cat','cats') → ADDITIVE
 * rhyme1 [AE1 T] vs rhyme2 [AE1 T S] - rhyme2 is longer
 */
function isAdditive(rhyme1: string[], rhyme2: string[]): boolean {
  // rhyme2 must be exactly one phoneme longer than rhyme1
  if (rhyme2.length - rhyme1.length !== 1) return false;

  // All phonemes of rhyme1 must match beginning of rhyme2
  for (let i = 0; i < rhyme1.length; i++) {
    if (stripStress(rhyme1[i]) !== stripStress(rhyme2[i])) {
      return false;
    }
  }

  return true;
}

/**
 * Check for subtractive rhyme (one phoneme removed from coda)
 * Example: detectRhymeCategory('cats','cat') → SUBTRACTIVE
 * rhyme1 [AE1 T S] vs rhyme2 [AE1 T] - rhyme2 is shorter
 */
function isSubtractive(rhyme1: string[], rhyme2: string[]): boolean {
  // rhyme1 must be exactly one phoneme longer than rhyme2
  if (rhyme1.length - rhyme2.length !== 1) return false;

  // All phonemes of rhyme2 must match beginning of rhyme1
  for (let i = 0; i < rhyme2.length; i++) {
    if (stripStress(rhyme1[i]) !== stripStress(rhyme2[i])) {
      return false;
    }
  }

  return true;
}

/**
 * Check for assonance (stressed vowel matches, consonants differ)
 * First phoneme (stressed vowel) must match, rest must differ
 */
function isAssonance(rhyme1: string[], rhyme2: string[]): boolean {
  // Both must start with a vowel (the stressed vowel)
  if (rhyme1.length === 0 || rhyme2.length === 0) return false;
  if (!isVowel(rhyme1[0]) || !isVowel(rhyme2[0])) return false;

  // First vowel must match (ignoring stress level)
  if (stripStress(rhyme1[0]) !== stripStress(rhyme2[0])) return false;

  // Coda (consonants after vowel) must differ
  // If both are just the vowel, that's a perfect rhyme, not assonance
  if (rhyme1.length === 1 && rhyme2.length === 1) return false;

  // Extract coda (everything after the vowel)
  const coda1 = rhyme1.slice(1);
  const coda2 = rhyme2.slice(1);

  // Codas must exist and differ
  if (coda1.length === 0 && coda2.length === 0) return false;

  return !phonemesMatch(coda1, coda2);
}

/**
 * Check for consonance (final consonant matches, vowel differs)
 */
function isConsonance(rhyme1: string[], rhyme2: string[]): boolean {
  // Both must end with a consonant
  if (rhyme1.length === 0 || rhyme2.length === 0) return false;

  const last1 = rhyme1[rhyme1.length - 1];
  const last2 = rhyme2[rhyme2.length - 1];

  if (!isConsonant(last1) || !isConsonant(last2)) return false;

  // Final consonant must match
  if (stripStress(last1) !== stripStress(last2)) return false;

  // Vowels (nucleus) must differ
  // Find the first vowel in each rhyming portion
  const vowel1 = rhyme1.find(isVowel);
  const vowel2 = rhyme2.find(isVowel);

  // Both must have vowels
  if (!vowel1 || !vowel2) return false;

  // Vowels must differ
  return stripStress(vowel1) !== stripStress(vowel2);
}
